import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageSEO from '@/components/feature/PageSEO';
import VideoCard from './components/VideoCard';
import ArticleCard from './components/ArticleCard';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filteredVideos = useMemo(() => {
    if (activeCategory === 'all') return videos;
    return videos.filter((v) => v.category === activeCategory);
  }, [activeCategory]);

  const hotVideos = useMemo(() => videos.filter((v) => v.isHot).slice(0, 6), []);
  const newVideos = useMemo(() => videos.filter((v) => v.isNew).slice(0, 8), []);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '番茄视频',
    url: 'https://www.fanqieshipin.com',
    description: '汇集西瓜视频免费、91在线视频、小蓝视频、久久视频等平台资源，提供电影电视剧全集免费在线观看。',
    sameAs: [
      'https://www.fanqieshipin.com',
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '番茄视频',
    url: 'https://www.fanqieshipin.com',
    potentialAction: {
      '@type': 'SearchAction',
      'target': 'https://www.fanqieshipin.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '番茄视频 - 首页',
    description: '番茄视频📣汇集西瓜视频免费、91在线视频、小蓝视频等平台资源，提供电影电视剧全集免费在线观看。',
    url: 'https://www.fanqieshipin.com/',
  };

  return (
    <>
      <PageSEO
        title="西瓜视频免费与91在线视频免费在线观看 | 小蓝视频久久视频免费看电视剧全集"
        description="番茄视频📣汇集西瓜视频免费、91在线视频、💬小蓝视频等平台资源，提供电影电视剧全集免费在线观看，无需付费，即点即播不缓冲，畅享高清免费影视盛宴。"
        keywords="番茄视频,西瓜视频免费,91在线视频,91视频网站,小蓝视频,久久视频,免费在线观看,免费版,在线播放,免费看,高清,不缓冲"
        canonicalUrl="/"
        schemaData={[organizationSchema, websiteSchema, webPageSchema]}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[450px] md:h-[580px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20cinematic%20dreamlike%20abstract%20composition%20with%20flowing%20warm%20gradients%20of%20deep%20crimson%20orange%20and%20soft%20gold%2C%20smooth%20curved%20ribbon-like%20forms%20intertwining%20in%20dark%20space%2C%20elegant%20modern%20minimal%20aesthetic%2C%20luxurious%20theater-like%20ambiance%2C%20no%20text%2C%20no%20people%2C%20no%20real%20objects%2C%20pure%20abstract%20art%20with%20cinematic%20lighting&width=1600&height=900&seq=hero-bg-2024&orientation=landscape"
            alt="番茄视频 - 免费高清影视平台"
            className="absolute inset-0 w-full h-full object-cover"
            title="番茄视频 - 免费高清影视平台"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-5">
              <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center">
                <i className="ri-play-fill text-white text-2xl"></i>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold font-heading text-white">
                番茄视频
              </h1>
            </div>
            <p className="text-lg md:text-xl text-white/90 mb-3 leading-relaxed">
              汇聚<strong className="text-white">西瓜视频免费</strong>、<strong className="text-white">91在线视频</strong>、<strong className="text-white">小蓝视频</strong>等平台资源
            </p>
            <p className="text-base md:text-lg text-white/70 mb-8 leading-relaxed">
              电影电视剧全集<strong className="text-white">免费在线观看</strong>，无需付费，即点即播不缓冲，畅享<strong className="text-white">高清免费影视</strong>盛宴
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/category/dianying"
                className="px-6 py-3 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-film-line mr-2"></i>看电影
              </Link>
              <Link
                to="/category/dianshiju"
                className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 text-white font-semibold text-sm backdrop-blur-sm transition-all duration-300 cursor-pointer border border-white/20 whitespace-nowrap"
              >
                <i className="ri-tv-line mr-2"></i>追剧集
              </Link>
              <Link
                to="/category/dongman"
                className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 text-white font-semibold text-sm backdrop-blur-sm transition-all duration-300 cursor-pointer border border-white/20 whitespace-nowrap"
              >
                <i className="ri-ghost-line mr-2"></i>看动漫
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-background-50 border-b border-background-200">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: '10,000+', label: '影视资源', icon: 'ri-video-line' },
              { value: '50+', label: '合作平台', icon: 'ri-link-m' },
              { value: '1000万+', label: '日活用户', icon: 'ri-user-line' },
              { value: '4K', label: '超清画质', icon: 'ri-4k-line' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-center gap-3 justify-center">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${stat.icon} text-accent-600 text-lg`}></i>
                </div>
                <div>
                  <p className="text-lg font-bold text-foreground-900 whitespace-nowrap">{stat.value}</p>
                  <p className="text-xs text-foreground-500 whitespace-nowrap">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Category Filter */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </button>
            ))}
          </div>
        </section>

        {/* Video Grid */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 pb-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
            {filteredVideos.slice(0, 18).map((video) => (
              <VideoCard key={video.id} {...video} />
            ))}
          </div>
          {filteredVideos.length === 0 && (
            <div className="text-center py-16 text-foreground-500">
              <i className="ri-inbox-line text-4xl mb-3 block"></i>
              <p>暂无相关内容</p>
            </div>
          )}
        </section>

        {/* Hot Recommended */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 rounded-full bg-primary-500"></div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  🔥 热门推荐
                </h2>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {hotVideos.map((video) => (
                <VideoCard key={video.id} {...video} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 rounded-full bg-accent-500"></div>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                🆕 最新上线
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {newVideos.map((video) => (
              <VideoCard key={video.id} {...video} />
            ))}
          </div>
        </section>

        {/* Platform Links Banner */}
        <section className="bg-gradient-to-r from-primary-500 to-accent-500 py-10 md:py-12">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-white mb-3">
              多平台资源一站聚合
            </h2>
            <p className="text-white/85 mb-6 max-w-2xl mx-auto">
              汇聚西瓜视频免费、91在线视频、小蓝视频、久久视频等主流平台，海量影视资源一网打尽
            </p>
            <div className="flex items-center justify-center gap-3 md:gap-5 flex-wrap">
              {['西瓜视频免费', '91在线视频', '小蓝视频', '久久视频'].map((platform) => (
                <div
                  key={platform}
                  className="px-5 py-3 rounded-xl bg-white/15 backdrop-blur-sm text-white font-semibold text-sm border border-white/20 whitespace-nowrap"
                >
                  {platform}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Section */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 rounded-full bg-secondary-500"></div>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                📰 影视资讯
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {articles.slice(0, 4).map((article) => (
              <ArticleCard key={article.id} {...article} />
            ))}
          </div>
          {articles.length > 4 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {articles.slice(4).map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          )}
        </section>

        {/* FAQ Section */}
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-3 mb-8 justify-center">
              <div className="w-1 h-6 rounded-full bg-primary-500"></div>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                常见问题
              </h2>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {[
                {
                  q: '番茄视频是否需要付费？',
                  a: '完全不需要！番茄视频汇集多家平台免费资源，所有内容均可免费在线观看，无需注册，无需付费。',
                },
                {
                  q: '视频画质怎么样？是否支持高清？',
                  a: '番茄视频提供的所有内容均支持高清画质，部分热门内容支持超清4K。同时采用先进加速技术，确保即点即播不缓冲。',
                },
                {
                  q: '可以在手机上观看吗？',
                  a: '当然可以！番茄视频完美适配电脑、平板和手机端，随时随地打开浏览器即可畅享高清影视盛宴。',
                },
                {
                  q: '资源更新速度快吗？',
                  a: '番茄视频与西瓜视频免费、91在线视频、小蓝视频、久久视频等平台保持同步更新，热门电影和电视剧第一时间上线。',
                },
              ].map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer"
                >
                  <summary className="px-5 py-4 flex items-center justify-between text-foreground-900 font-medium text-sm list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform text-lg"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
            立即开始免费观影
          </h2>
          <p className="text-foreground-600 mb-6 max-w-xl mx-auto">
            数千部高清电影、热门电视剧、精彩综艺动画，尽在番茄视频。无需付费，打开即看！
          </p>
          <Link
            to="/category/all"
            className="inline-flex px-8 py-3.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-play-circle-line mr-2 text-base"></i>开始观看
          </Link>
        </section>
      </main>

      <Footer />
    </>
  );
}